import React, { useEffect, useState } from 'react';
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import type {
  GoalLength,
  NextEpisodeResponse,
  PatientGoalDetail,
} from '../../services/api/types.gen';
import { extendGoalToNextEpisode } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  formatDateForDisplay,
  formatDateToISO,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { type PushGoalFieldValues } from '../../utils/goals';
import PushGoalFields from './PushGoalFields';

const PUSH_WARNING_MESSAGE =
  "This pushes the goal and its interventions to the patient's next episode. " +
  'Once pushed, you can no longer edit this goal or its interventions for the ' +
  'rest of the episode. This cannot be undone. To change interventions first, ' +
  'click Cancel and update them before pushing.';

interface PushGoalDialogProps {
  goal: PatientGoalDetail;
  visitId: string;
  nextEpisode: NextEpisodeResponse;
  /** Confirmation step is open. */
  confirmOpen: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Two-step "Push to next episode" flow: a confirmation dialog,
 * then a re-target form for the copy landing in the next episode. Split out
 * of DisplayGoalCard.tsx (already large) and wired in as a controlled child.
 *
 * Gating (visit editable, progress-trackable, next episode exists, goal not
 * met/unattainable/already extended) is fully handled by the caller before
 * this even opens, so the form step can assume `nextEpisode` is a real
 * target and doesn't need to re-handle the "no next episode" case.
 */
const PushGoalDialog: React.FC<PushGoalDialogProps> = ({
  goal,
  visitId,
  nextEpisode,
  confirmOpen,
  onCancel,
  onSuccess,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [step, setStep] = useState<'confirm' | 'form'>('confirm');
  const [instruction, setInstruction] = useState('');
  const [length, setLength] = useState<GoalLength>('short_term');
  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
  const [isForEndOfEpisode, setIsForEndOfEpisode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const minDate = parseISOToLocalDate(nextEpisode.start_date);
  const maxDate = parseISOToLocalDate(nextEpisode.end_date);

  useEffect(() => {
    if (confirmOpen) {
      setStep('confirm');
      setInstruction(goal.instruction ?? '');
      setLength(goal.length ?? 'short_term');
      setTargetDate(undefined);
      setIsForEndOfEpisode(false);
    }
    // Depend on goal.id, not the goal object: a background goals refresh
    // replaces every goal's object identity, and resetting then would wipe
    // the user's in-progress edits and bounce them back to the confirm step.
    // goal.instruction/length are only read to seed defaults at open time.
  }, [confirmOpen, goal.id]);

  const handleConfirmAccept = () => {
    setStep('form');
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    onCancel();
  };

  const handleFieldsChange = (values: PushGoalFieldValues) => {
    setInstruction(values.instruction);
    setLength(values.length);
    setTargetDate(values.targetDate);
    setIsForEndOfEpisode(values.isForEndOfEpisode);
  };

  const isFormValid = !!instruction.trim() && !!targetDate;

  const handleSubmit = () => {
    if (!targetDate || !instruction.trim()) return;
    setIsSubmitting(true);
    extendGoalToNextEpisode({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      body: {
        instruction: instruction.trim(),
        length,
        target_date: formatDateToISO(targetDate),
        is_for_end_of_episode: isForEndOfEpisode,
      },
      throwOnError: true,
    })
      .then(() => {
        showSuccess(
          'Goal pushed',
          `"${goal.name}" was pushed to the next episode.`
        );
        onSuccess();
      })
      .catch(error => {
        console.error(error);
        showError(
          'Failed to push goal',
          getBusinessErrorMessage(error) ??
            `Failed to push goal "${goal.name}" to the next episode.`
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <ConfirmationDialog
        open={confirmOpen && step === 'confirm'}
        title='Push goal to next episode?'
        message={PUSH_WARNING_MESSAGE}
        confirmLabel='Continue'
        onAccept={handleConfirmAccept}
        onCancel={handleCancel}
      />
      <Dialog
        open={confirmOpen && step === 'form'}
        onOpenChange={isOpen => !isOpen && handleCancel()}
      >
        <DialogContent className='sm:max-w-[480px]'>
          <DialogHeader>
            <DialogTitle>Push &quot;{goal.name}&quot;</DialogTitle>
            <DialogDescription>
              Set the instruction, type, and target date for the copy landing in
              the next episode ({formatEpisodeRange(nextEpisode)}).
            </DialogDescription>
          </DialogHeader>

          <PushGoalFields
            idPrefix={`push-${goal.id}`}
            values={{ instruction, length, targetDate, isForEndOfEpisode }}
            onChange={handleFieldsChange}
            minDate={minDate}
            maxDate={maxDate}
            endOfEpisodeLabel='For end of next episode'
          />

          <DialogFooter>
            <Button
              variant='outline'
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? 'Pushing…' : 'Push goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

function formatEpisodeRange(nextEpisode: NextEpisodeResponse): string {
  return `${formatDateForDisplay(nextEpisode.start_date)} – ${formatDateForDisplay(nextEpisode.end_date)}`;
}

export default PushGoalDialog;
